/**
 * SNT-5 — apply registry fallbacks when typed narrative fields are
 * empty or instruction-shaped. Used at post-gen (siteRagService) and
 * optionally at render time when SED + site are available.
 */
import type { ReportTypeId } from '../config/reportTypes';
import type { SiteEntity } from '../types/site';
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import { detectInstructionShape } from './instructionShapeGuard';
import { buildProjectIntentOverview } from './buildProjectIntentOverview';
import { safeExecSummarySnippet } from './safeNarrativeProse';
import { SECTION_FALLBACKS, REPORT_DERIVED_FALLBACK_PATHS } from './sectionFallbacks';
import { applyTestFitRiskRegisterFallback } from './sectionFallbacks/testFit';
import { getReportFieldValue, hasSectionFieldContent, setReportFieldValue } from './sectionFieldPaths';
import { withheldNarrativeLeaves } from './report/withheldNarrative';
import type { SiteRagReport } from '../types/siteRag';

export type SectionGuardStatus = 'degraded-instruction-leak' | 'fallback-data-only';

export interface ApplySectionFallbacksResult {
  /** Shallow-cloned record with fallbacks applied on the clone. */
  data: Record<string, unknown>;
  sectionGuardStatus: Record<string, SectionGuardStatus>;
  quarantinedOriginal: Record<string, string>;
  appliedCount: number;
}

/** Minimal SiteEntity for fallback composers — only fields the SNT-5 helpers read. */
export function minimalSiteForFallbacks(input: {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  siteArea?: number;
}): SiteEntity {
  return ({
    id: input.id,
    name: input.name,
    address: input.address,
    location: { lat: input.lat, lng: input.lng },
    siteArea: input.siteArea,
    reportIds: [],
    simulationIds: [],
    documentIds: [],
  } as unknown) as SiteEntity;
}

/**
 * Walk `SECTION_FALLBACKS[reportType]` and fill missing or
 * instruction-leak strings. Skips array field paths (`[]`).
 */
export function applySectionFallbacks(
  reportType: ReportTypeId,
  data: Record<string, unknown>,
  site: SiteEntity,
  sed: SiteEvidenceDocument,
): ApplySectionFallbacksResult {
  const fallbacks = SECTION_FALLBACKS[reportType];
  const sectionGuardStatus: Record<string, SectionGuardStatus> = {};
  const quarantinedOriginal: Record<string, string> = {};
  let appliedCount = 0;
  // A leaf the reviewer-voice gate WITHHELD is an open, not-assessed item.
  // Back-filling it with synthesized SED text would present the gap as an
  // assessment, so withheld paths are never fallback targets; render shows
  // the honest "not assessed" notice there instead.
  const withheldPaths = new Set(
    withheldNarrativeLeaves(data as Pick<SiteRagReport, 'qualityWarnings'>)
      .map((leaf) => leaf.path),
  );

  const cloned = (() => {
    try {
      return structuredClone(data) as Record<string, unknown>;
    } catch {
      return JSON.parse(JSON.stringify(data)) as Record<string, unknown>;
    }
  })();

  // Top-level projectIntentOverview — rebuild from intent fields, not SED composers.
  if (!withheldPaths.has('projectIntentOverview')) {
    const current = cloned.projectIntentOverview;
    const currentStr = typeof current === 'string' ? current : undefined;
    const isLeak = currentStr !== undefined
      && currentStr.trim().length > 0
      && detectInstructionShape(currentStr).flagged;
    const isEmpty = !currentStr?.trim();

    if (isEmpty || isLeak) {
      const rebuilt = buildProjectIntentOverview({
        siteName: String(cloned.siteName ?? site.name ?? ''),
        address: typeof cloned.address === 'string' ? cloned.address : site.address,
        projectIntent: cloned.projectIntentAtGen as import('../types/siteRag').ProjectIntent | undefined,
        executiveSummarySnippet: safeExecSummarySnippet({
          executiveSummary: typeof cloned.executiveSummary === 'string' ? cloned.executiveSummary : undefined,
          sectionGuardStatus: cloned.sectionGuardStatus as Record<string, SectionGuardStatus> | undefined,
        }),
        reportType,
        powerSetup: reportType === 'power-availability'
          ? (cloned.typedPreferences as import('../types/siteRag').TypedPreferencesByType | undefined)?.['power-availability']
          : undefined,
      });
      if (rebuilt?.trim()) {
        cloned.projectIntentOverview = rebuilt;
        if (isLeak && currentStr) {
          sectionGuardStatus.projectIntentOverview = 'degraded-instruction-leak';
          quarantinedOriginal.projectIntentOverview = currentStr;
        } else {
          sectionGuardStatus.projectIntentOverview = 'fallback-data-only';
        }
        appliedCount += 1;
      }
    }
  }

  if (!fallbacks) {
    return { data: cloned, sectionGuardStatus, quarantinedOriginal, appliedCount };
  }

  for (const [fieldPath, composer] of Object.entries(fallbacks)) {
    if (fieldPath.includes('[]') || REPORT_DERIVED_FALLBACK_PATHS.has(fieldPath)) continue;
    if (withheldPaths.has(fieldPath)) continue;

    const current = getReportFieldValue(cloned, fieldPath);
    const currentStr = typeof current === 'string' ? current : undefined;

    if (currentStr !== undefined && currentStr.trim().length > 0) {
      const guard = detectInstructionShape(currentStr);
      if (guard.flagged) {
        const fallbackText = composer(site, sed);
        if (typeof fallbackText === 'string' && fallbackText.trim().length > 0) {
          setReportFieldValue(cloned, fieldPath, fallbackText);
          sectionGuardStatus[fieldPath] = 'degraded-instruction-leak';
          quarantinedOriginal[fieldPath] = currentStr;
          appliedCount += 1;
        }
      }
      continue;
    }

    if (hasSectionFieldContent(current)) continue;

    const fallbackText = composer(site, sed);
    if (typeof fallbackText !== 'string' || fallbackText.trim().length === 0) continue;
    setReportFieldValue(cloned, fieldPath, fallbackText);
    sectionGuardStatus[fieldPath] = 'fallback-data-only';
    appliedCount += 1;
  }

  // SNT-5 risk register (QA 2026-06-10) — test-fit reports shipped with an
  // empty risk register (conclusion.topRisks / risks / recommendations all
  // []) while verdictLean.drivers named decision-grade risks. These are
  // ARRAY fields, so the string-composer loop above cannot fill them; the
  // dedicated composer derives them from verdictLean.drivers + failed
  // cross-validation findings, filling ONLY fields the model left empty.
  if (reportType === 'test-fit') {
    const riskRegisterPaths = applyTestFitRiskRegisterFallback(cloned);
    for (const path of riskRegisterPaths) {
      sectionGuardStatus[path] = 'fallback-data-only';
      appliedCount += 1;
    }
  }

  return { data: cloned, sectionGuardStatus, quarantinedOriginal, appliedCount };
}

/**
 * Render-time helper — merge registry fallbacks into a report snapshot
 * when SED + site are available. Identity when nothing applies.
 */
export function applySectionFallbacksToReport<T extends Record<string, unknown>>(
  reportType: ReportTypeId,
  report: T,
  site: SiteEntity,
  sed: SiteEvidenceDocument,
): T {
  const applied = applySectionFallbacks(reportType, report, site, sed);
  if (applied.appliedCount === 0) return report;
  return {
    ...report,
    ...applied.data,
    sectionGuardStatus: {
      ...((report.sectionGuardStatus as Record<string, SectionGuardStatus> | undefined) ?? {}),
      ...applied.sectionGuardStatus,
    },
    quarantinedOriginal: {
      ...((report.quarantinedOriginal as Record<string, string> | undefined) ?? {}),
      ...applied.quarantinedOriginal,
    },
  } as T;
}
